import { normalizeSections } from '~/composables/useEmailSections'

/** Strip HTML tags + decode the handful of entities that show up in email copy. */
function stripHtml(html: string): string {
  return (html || '')
    .replace(/<br\s*\/?>/gi, '\n')
    .replace(/<\/(p|div|h[1-6]|li)>/gi, '\n')
    .replace(/<[^>]+>/g, '')
    .replace(/&nbsp;/g, ' ')
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&#39;|&rsquo;|&lsquo;/g, "'")
    .replace(/&quot;|&ldquo;|&rdquo;/g, '"')
    .replace(/[ \t]+\n/g, '\n')
    .trim()
}

function resolveLink(cell: EmailImageT, group?: EmailGroupT | null): string {
  let link = cell.link || ''
  if (!link) return ''
  // Append configured tracking params if the org has them and the link has none.
  const params = (group as any)?.linkParams || (group as any)?.link_params
  if (params && !link.includes('?') && /^https?:\/\//.test(link)) {
    link += (link.includes('?') ? '&' : '?') + params
  }
  return link
}

/**
 * Assemble a plaintext version of an email from its typed sections. Image bands contribute
 * their (OCR-generated) alt text plus link URL, so a well-sliced email produces a genuinely
 * useful text/plain alternative — better inbox placement + accessibility.
 */
export function buildEmailText(email: EmailT | null, group?: EmailGroupT | null): string {
  if (!email) return ''
  const lines: string[] = []

  if (email.subject) lines.push(email.subject)
  if (email.previewText) lines.push(email.previewText)
  if (lines.length) lines.push('')

  const sections = normalizeSections(email.sections || [])
  for (const s of sections) {
    switch (s.type) {
      case 'text': {
        if (s.heading) lines.push(stripHtml(s.heading))
        if (s.body) lines.push(stripHtml(s.body))
        lines.push('')
        break
      }
      case 'image': {
        for (const c of s.cells || []) {
          const alt = (c.alt || '').trim()
          const link = resolveLink(c, group)
          if (alt && link) lines.push(`${alt}\n  ${link}`)
          else if (alt) lines.push(alt)
          else if (link) lines.push(link)
        }
        lines.push('')
        break
      }
      case 'button': {
        const label = s.buttonText || 'Button'
        const url = s.buttonUrl || ''
        lines.push(url ? `[ ${label} ] ${url}` : `[ ${label} ]`)
        lines.push('')
        break
      }
      case 'html': {
        const text = stripHtml(s.html || '')
        if (text) lines.push(text)
        lines.push('')
        break
      }
      case 'divider': {
        lines.push('—'.repeat(30))
        break
      }
      case 'spacer':
      default:
        break
    }
  }

  if (group?.name) {
    lines.push('')
    lines.push(group.name)
  }

  return lines
    .join('\n')
    .replace(/\n{3,}/g, '\n\n')
    .trim()
}
